/* Phase 4 — Twilio Dialer global context.
 *
 * Lazy-initializes the @twilio/voice-sdk Device the first time the agent
 * opens the panel or fires a click-to-call. Owns:
 *   - Device + active Call connection lifecycle
 *   - Token refresh (50-minute interval)
 *   - Panel open/close state
 *   - Mute, hangup, DTMF
 *   - Pending click-to-call queue (in case user clicks before Device is ready)
 *
 * Consumers: useDialer().placeCall({ to, lead }) from anywhere in the admin shell.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Device } from "@twilio/voice-sdk";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";

const TOKEN_REFRESH_MS = 50 * 60 * 1000;

/**
 * Strip every character that isn't allowed in a dial string.
 * Allowed: + (only at start), 0-9, *, #
 */
export function sanitizeDialString(raw) {
    if (!raw) return "";
    let s = String(raw).replace(/[^\d+*#]/g, "");
    // `+` is only valid at index 0
    s = s.replace(/\+/g, (match, idx) => (idx === 0 ? "+" : ""));
    return s;
}

/**
 * Normalize a user-entered number into E.164 for placement.
 *  - already starts with `+`: keep
 *  - 11 digits starting with 1: prepend `+`
 *  - 10 digits: prepend `+1`
 *  - else: pass through (Twilio will reject; we surface the error)
 */
export function normalizePhone(raw) {
    const s = sanitizeDialString(raw);
    if (!s) return "";
    if (s.startsWith("+")) return s;
    if (/^1\d{10}$/.test(s)) return `+${s}`;
    if (/^\d{10}$/.test(s)) return `+1${s}`;
    return s;
}

const DialerCtx = createContext(null);

export const useDialer = () => {
    const ctx = useContext(DialerCtx);
    if (!ctx) throw new Error("useDialer must be used within <DialerProvider>");
    return ctx;
};

export function DialerProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | initializing | ringing | in_call | incoming | post_call
    const [error, setError] = useState(null);
    const [muted, setMuted] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [currentTo, setCurrentTo] = useState("");
    const [callTimer, setCallTimer] = useState(0);
    const [identity, setIdentity] = useState("");
    const [usedToday, setUsedToday] = useState(0);
    const [dailyCap, setDailyCap] = useState(100);
    const [recentCalls, setRecentCalls] = useState([]);

    // Phase 5 — post_call state holds the just-ended call context so the
    // panel can surface a Call Log form before returning to idle.
    const [lastCall, setLastCall] = useState(null);

    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const tokenTimerRef = useRef(null);
    const callTimerRef = useRef(null);
    const pendingCallRef = useRef(null);
    const callStartRef = useRef(null);
    const inCallNotesRef = useRef("");

    const reset = useCallback(() => {
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
        callRef.current = null;
        setStatus("idle");
        setMuted(false);
        setCallTimer(0);
        setError(null);
        setLastCall(null);
    }, []);

    // Phase 5 — after a call disconnects, freeze a snapshot + transition to post_call.
    const transitionToPostCall = useCallback((finalTimer) => {
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
        const durationSec = finalTimer ?? callTimer ?? 0;
        // Wait 300ms to let Twilio persist the call row, then capture the id.
        setTimeout(async () => {
            let callId = null;
            try {
                const r = await adminApi.calls({ page_size: 5 });
                callId = (r.items || [])[0]?.id || null;
            } catch { /* ignore */ }
            setLastCall({
                call_id: callId,
                to: currentTo,
                lead: currentLead,
                duration_seconds: durationSec,
                direction: "outbound",
                started_at: callStartRef.current,
                ended_at: new Date().toISOString(),
                prefill_notes: inCallNotesRef.current || "",
            });
            callStartRef.current = null;
            setStatus("post_call");
        }, 350);
        callRef.current = null;
        setMuted(false);
    }, [callTimer, currentTo, currentLead]);

    // Notes sync hook — DialerPanel InCallState writes here so the post-call
    // form can pre-populate with the agent's live notes.
    const setInCallNotes = useCallback((v) => {
        inCallNotesRef.current = v || "";
    }, []);

    // Called by PostCallState when agent submits or skips. Returns to idle.
    const completePostCall = useCallback(() => {
        setLastCall(null);
        setStatus("idle");
    }, []);

    const refreshUsage = useCallback(async () => {
        try {
            const r = await adminApi.calls({ page_size: 8 });
            setRecentCalls(r.items || []);
        } catch { /* ignore */ }
    }, []);

    const fetchToken = useCallback(async () => {
        const r = await adminApi.dialerAccessToken();
        setIdentity(r.identity || "");
        setUsedToday(r.used_today ?? 0);
        setDailyCap(r.daily_cap ?? 100);
        return r.token;
    }, []);

    const setupDevice = useCallback(async () => {
        if (deviceRef.current) return deviceRef.current;
        setStatus("initializing");
        try {
            const token = await fetchToken();
            const device = new Device(token, {
                codecPreferences: ["opus", "pcmu"],
                logLevel: 2,
                allowIncomingWhileBusy: false,
            });
            device.on("registered", () => {
                setStatus((s) => (s === "initializing" ? "idle" : s));
            });
            device.on("error", (e) => {
                console.warn("[dialer] device error:", e);
                setError(e?.message || String(e));
                if (callRef.current) reset();
            });
            device.on("incoming", (incoming) => {
                callRef.current = incoming;
                setStatus("incoming");
                setOpen(true);
                setCurrentTo(incoming.parameters?.From || "Unknown");
                wireCallEvents(incoming);
            });
            device.on("tokenWillExpire", async () => {
                try {
                    const t = await fetchToken();
                    device.updateToken(t);
                } catch (e) {
                    console.warn("[dialer] token refresh failed", e);
                }
            });
            await device.register();
            deviceRef.current = device;

            tokenTimerRef.current = setInterval(async () => {
                try {
                    const t = await fetchToken();
                    device.updateToken(t);
                } catch { /* ignore */ }
            }, TOKEN_REFRESH_MS);
            setStatus("idle");
            return device;
        } catch (e) {
            console.warn("[dialer] device setup failed", e);
            const detail =
                e?.response?.data?.detail || e?.message || "Couldn't initialize the dialer.";
            setError(detail);
            setStatus("idle");
            toast.error(detail);
            throw e;
        }
         
    }, [fetchToken, reset]);

    const wireCallEvents = useCallback((call) => {
        call.on("accept", () => {
            setStatus("in_call");
            setCallTimer(0);
            callStartRef.current = new Date().toISOString();
            inCallNotesRef.current = "";
            callTimerRef.current = setInterval(() => {
                setCallTimer((t) => t + 1);
            }, 1000);
        });
        call.on("ringing", () => setStatus("ringing"));
        call.on("disconnect", () => {
            // If we've been in_call at least once, go to post_call; else just reset.
            if (callStartRef.current) {
                // read latest timer via closure
                setCallTimer((t) => {
                    transitionToPostCall(t);
                    return t;
                });
            } else {
                reset();
            }
            refreshUsage();
        });
        call.on("cancel", reset);
        call.on("reject", reset);
        call.on("error", (e) => {
            console.warn("[dialer] call error:", e);
            setError(e?.message || String(e));
            if (callStartRef.current) {
                setCallTimer((t) => {
                    transitionToPostCall(t);
                    return t;
                });
            } else {
                reset();
            }
        });
    }, [reset, refreshUsage, transitionToPostCall]);

    const placeCall = useCallback(async ({ to, lead } = {}) => {
        const rawInput = (to || lead?.phone || "").trim();
        const e164 = normalizePhone(rawInput);
        if (!e164) {
            toast.error("No phone number provided.");
            return;
        }
        setOpen(true);
        setError(null);
        setCurrentLead(lead || null);
        setCurrentTo(e164);
        try {
            await setupDevice();
        } catch {
            return;
        }
        try {
            const params = { To: e164 };
            if (lead?.id) params.LeadId = lead.id;
            const call = await deviceRef.current.connect({ params });
            callRef.current = call;
            wireCallEvents(call);
        } catch (e) {
            console.warn("[dialer] connect failed", e);
            setError(e?.message || "Couldn't place the call.");
            toast.error("Couldn't place the call.");
        }
    }, [setupDevice, wireCallEvents]);

    const hangup = useCallback(() => {
        try {
            callRef.current?.disconnect();
        } catch { /* ignore */ }
        reset();
    }, [reset]);

    const toggleMute = useCallback(() => {
        if (!callRef.current) return;
        const next = !muted;
        callRef.current.mute(next);
        setMuted(next);
    }, [muted]);

    const sendDigit = useCallback((d) => {
        if (callRef.current && status === "in_call") {
            try { callRef.current.sendDigits(String(d)); } catch { /* ignore */ }
        } else {
            // Idle: append to dial pad
            setCurrentTo((s) => (s.length < 18 ? s + String(d) : s));
        }
    }, [status]);

    const acceptIncoming = useCallback(() => {
        try {
            callRef.current?.accept();
        } catch (e) {
            setError(e?.message || "Couldn't accept the call.");
        }
    }, []);

    const rejectIncoming = useCallback(() => {
        try {
            callRef.current?.reject();
        } catch { /* ignore */ }
        reset();
    }, [reset]);

    const openPanel = useCallback(() => {
        setOpen(true);
        if (!deviceRef.current) {
            setupDevice().catch(() => {});
        }
    }, [setupDevice]);

    const closePanel = useCallback(() => {
        if (status === "in_call" || status === "ringing") return; // can't close mid-call
        setOpen(false);
    }, [status]);

    const togglePanel = useCallback(() => {
        if (open) closePanel();
        else openPanel();
    }, [open, openPanel, closePanel]);

    // Keep usage fresh while open
    useEffect(() => {
        if (open) refreshUsage();
    }, [open, refreshUsage]);

    // Cleanup on unmount
    useEffect(() => () => {
        try { deviceRef.current?.destroy(); } catch { /* ignore */ }
        if (tokenTimerRef.current) clearInterval(tokenTimerRef.current);
        if (callTimerRef.current) clearInterval(callTimerRef.current);
    }, []);

    // Esc to close (only when not in a call)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open && status === "idle") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, status]);

    // Phase 4 hot patch — suppress the Twilio SDK's `setSinkId` user-gesture
    // rejection. Some browsers (Safari, occasionally Chrome) require a user
    // gesture to call setSinkId on the SDK's internal audio elements. The SDK
    // attempts it during ringtone init; if the call is initiated programmatically
    // (e.g. via click-to-call from a lead row or from an Enter keypress that
    // races the audio-helper init), the gesture context can be lost and the
    // promise rejects. The actual call still connects via the browser default
    // output device, so we silently absorb the warning and keep the UI clean.
    useEffect(() => {
        const isAudioGestureNoise = (msg) =>
            !!msg && (
                msg.includes("setSinkId") ||
                msg.includes("user gesture is required") ||
                msg.includes("user activation is required")
            );
        const onUnhandledRejection = (e) => {
            const msg = String(e?.reason?.message || e?.reason || "");
            if (isAudioGestureNoise(msg)) {
                e.preventDefault();
                console.warn("[Dialer] Suppressed setSinkId user-gesture warning:", msg);
            }
        };
        const onWindowError = (e) => {
            const msg = String(e?.error?.message || e?.message || "");
            if (isAudioGestureNoise(msg)) {
                e.preventDefault();
                console.warn("[Dialer] Suppressed setSinkId user-gesture error:", msg);
            }
        };
        window.addEventListener("unhandledrejection", onUnhandledRejection);
        window.addEventListener("error", onWindowError);
        return () => {
            window.removeEventListener("unhandledrejection", onUnhandledRejection);
            window.removeEventListener("error", onWindowError);
        };
    }, []);

    const value = useMemo(() => ({
        open, status, error, muted, callTimer,
        identity, usedToday, dailyCap, recentCalls,
        currentLead, currentTo,
        lastCall, setInCallNotes, completePostCall,
        openPanel, closePanel, togglePanel,
        placeCall, hangup, toggleMute, sendDigit,
        acceptIncoming, rejectIncoming,
        setCurrentTo,
        refreshUsage,
        isReady: !!deviceRef.current,
    }), [
        open, status, error, muted, callTimer, identity, usedToday, dailyCap,
        recentCalls, currentLead, currentTo, lastCall, setInCallNotes, completePostCall,
        openPanel, closePanel, togglePanel, placeCall, hangup, toggleMute,
        sendDigit, acceptIncoming, rejectIncoming, refreshUsage,
    ]);

    return <DialerCtx.Provider value={value}>{children}</DialerCtx.Provider>;
}
